import styled from "@emotion/styled";
import { Text } from "styles/utility-components";

import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeperator,
  DropdownMenuTrigger,
} from "components/ui/dropdown_menu";
import { SidebarHeader } from "components/ui/sidebar/Sidebar";
import EllipsisText from "components/ui/tooltip/EllipsisText";
import { DeploymentEnvironment, getDeploymentEnvironment } from "lib/config";
import {
  RELEVANCY_SEARCH_NEW_USERS,
  useVariableValue,
} from "lib/featureFlags/generated/dvcVariableTypes";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { settingsRoutes } from "lib/routes";
import { performLogout } from "lib/userUtils";

// So in settings nav that back button takes you to the last non-settings page
const persistLastPage = () => {
  sessionStorage.setItem("lastMainNavPageVisited", window.location.pathname);
};

type HeaderProps = {
  isGuestUser: boolean;
};

const Header = ({ isGuestUser }: HeaderProps) => {
  const { data: currentUser } = useCurrentUser();
  // Showing prod for non-admin users just in case the condition is ever wrong
  const environment: DeploymentEnvironment =
    currentUser?.isAdmin === true ? getDeploymentEnvironment() : "production";

  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  return (
    <SidebarHeader>
      <DropdownMenu
        width="fit-content"
        side="right"
        modal={false}
        trigger={
          <HeaderDropdownTrigger
            buttonStyle="inverse"
            buttonType="primary"
            arrowIcon="chevronSelectorVertical"
            iconProps={{ color: "sysTextDefault" }}
          >
            <StotlesIcon
              environment={environment}
              icon="stotles"
              size={32}
              ariaLabel="Stotles Icon"
            />
            <TextWrapper>
              <EllipsisText
                tooltipProps={{ side: "right" }}
                ellipsisMethod="lineClamp"
                fontWeight="500"
              >
                {isGuestUser ? "Guest" : currentUser?.team.name ?? ""}
              </EllipsisText>
              <Text description ellipsis ellipsisMethod="lineClamp" textTransform="capitalize">
                {isGuestUser
                  ? "Preview mode"
                  : currentUser?.subscription === "freemium"
                    ? "free"
                    : currentUser?.subscription}
              </Text>
            </TextWrapper>
          </HeaderDropdownTrigger>
        }
      >
        {!isRelevancySearchEnabled && (
          <DropdownMenuItem
            key="signal-settings"
            as="a"
            icon="signal01"
            to={settingsRoutes.signalSettings}
            onClick={persistLastPage}
            disabled={isGuestUser}
          >
            Signal settings
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          key="account-settings"
          as="a"
          icon="settings01"
          to={settingsRoutes.subscription}
          onClick={persistLastPage}
          disabled={isGuestUser}
        >
          Account settings
        </DropdownMenuItem>
        <DropdownMenuItem
          key="notification-settings"
          as="a"
          icon="bell03"
          to={settingsRoutes.notifications}
          onClick={persistLastPage}
          disabled={isGuestUser}
        >
          Notification settings
        </DropdownMenuItem>
        <DropdownMenuItem
          key="manage-team"
          as="a"
          icon="users01"
          to={settingsRoutes.team}
          onClick={persistLastPage}
          disabled={isGuestUser}
        >
          Manage Team
        </DropdownMenuItem>
        <DropdownMenuSeperator key="separator" />
        <DropdownMenuItem key="logout" icon="logOut01" onClick={() => performLogout()}>
          Logout
        </DropdownMenuItem>
      </DropdownMenu>
    </SidebarHeader>
  );
};

const HeaderDropdownTrigger = styled(DropdownMenuTrigger)({
  height: "48px",
  padding: "7px", // invisible 1px border so actually 8px padding
  justifyContent: "space-between",
  overflow: "hidden",
});

const TextWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  width: "calc(var(--sidebar-width) - 16px - 16px - 40px - 16px - 16px)", // SidebarHeader, HeaderDropdownTrigger, StotlesIcon, chevron, gap
});

const StotlesIcon = styled(UIcon)<{ environment: DeploymentEnvironment }>(({ environment }) => ({
  marginRight: 8,

  ...(environment !== "production" && {
    "& path": {
      fill: environment === "sandbox" ? "#FFB24F" : "#00BF5F",
    },
  }),
}));

export default Header;
